import { memo, useRef } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'react-easyrocket';
import { DropDownOption } from 'src/types';
import { useDropdown } from 'src/hooks/useDropdown';
import { Arrow } from 'src/components/svg/Arrows';
import './styles.css';

interface DropdownProps {
  options?: DropDownOption[];
  value?: string | undefined;
  placeholder?: string;
  onChange?: (element?: string) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  labelWithPadding?: boolean;
  classNameArrow?: string;
  placeholderClassName?: string;
  labelClassName?: string;
}

const _Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  disabled,
  className,
  label,
  labelWithPadding,
  classNameArrow,
  placeholderClassName,
  labelClassName
}: DropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const {
    value: selectedValue,
    selectedOptionName,
    onSelect,
    close,
    toggle,
    showList
  } = useDropdown({
    value,
    onChange,
    options
  });
  const { t } = useTranslation();
  useClickOutside(dropdownRef, close);

  return (
    <div className="relative text-base w-full cursor-pointer z-10" ref={dropdownRef}>
      {label ? (
        <label
          className={twMerge(
            'text-base block mb-2 text-i_label',
            labelWithPadding && 'pl-5',
            labelClassName
          )}>
          {label}
        </label>
      ) : null}
      <div
        style={{ transform: 'translate3d(0, 0, 0)' }}
        className={classNames(
          'flex relative flex-row items-center h-10 z-[11] border border-solid placeholder:text-base placeholder:text-i_placeholder rounded-[20px] border-b-1 bg-i_dropdown_bg border-i_dropdown_border shrink-[0]',
          className && `${className}`,
          disabled && 'bg-i_bg_disabled cursor-not-allowed'
        )}
        onClick={() => disabled || toggle()}>
        {selectedOptionName || selectedValue || value ? (
          <p className="text-base text-i_text py-2.5 w-full pl-5 leading-5">
            {selectedOptionName || selectedValue || value}
          </p>
        ) : (
          <p
            className={twMerge(
              'text-base text-i_placeholder py-2.5 w-full 640:pl-5 pl-2 leading-5',
              placeholderClassName
            )}>
            {placeholder}
          </p>
        )}
        <Arrow
          fill="var(--i_dropdown_arrow)"
          className={classNames(
            'absolute right-2.5 w-[12px] h-full stroke-[3px] transition-transform will-change-transform',
            classNameArrow ? `${classNameArrow}` : '',
            { opened: showList }
          )}
        />
      </div>
      {showList && (
        <div
          className={classNames(
            'entrance absolute rounded-b-[20px] top-5 bg-i_dropdown_bg border border-solid border-i_dropdown_border z-10 flex flex-col items-start overflow-y-auto w-full max-h-[150px] pt-4 pb-2 shadow',
            label ? 'top-[59px]' : 'top-5'
          )}>
          {options.map((option) => (
            <div
              className="text-[14px] font-medium text-i_text cursor-pointer py-2 px-5 w-full r_xsm:px-2.5 hover:!text-i_dropdown_bg hover:bg-i_dropdown_hover"
              onClick={() => {
                onSelect(option.id, option);
              }}
              key={option.id}>
              {t(`${option.name}`)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Dropdown = memo(_Dropdown);
